/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useReducer } from 'react';
import { ResponsiveGrid } from '@adobe/aem-react-editable-components';
import Cookies from 'js-cookie';
import { ApolloProvider } from '@apollo/client';

import { addSubDirectoryPrefix, getProcessEnvs, preProcessorCallMocked } from '@marriott/mi-shop-components/utils';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { useGlobalStore } from '@marriott/mi-store-utils';

// import { mockModel } from '../../_localDevMocks/mockModel';

import { GetServerSideProps } from 'next';

import { getFeatureFlagData, prePageCall } from '@marriott/mi-headless-utils';
import { Layout, respGridUtil } from '@marriott/mi-headless-utils';
import operationSignatures from '../../_constants/operationSignatures.json';
import { getDeployedEnvType, useNextMiApolloClientWithoutCache } from '@marriott/mi-apollo-client-utils';
import { HQVContext, hqvInitialState, hqvReducer, ErrorHandlerAlert } from '@marriott/mi-shop-components';
import axios from 'axios';
import { inspect } from 'util';

export default function FindHotelPage({ model, pagePath, isAuthorMode }) {
  const MiApolloClient = useNextMiApolloClientWithoutCache(operationSignatures, getDeployedEnvType());

  const {
    metaNames,
    metaProperties,
    title,
    staticComponentClientLibsCSS,
    staticComponentClientLibsJS,
    gridCss,
    hreflangList,
    canonical,
  } = model;
  const respGridModel = respGridUtil(model?.cqItems?.root?.[':items']?.responsivegrid);
  const [state, dispatch] = useReducer(hqvReducer, hqvInitialState);

  return (
    <ErrorHandlerAlert alertMessage={model?.['componentErrorMessage']}>
      <ApolloProvider client={MiApolloClient}>
        <HQVContext.Provider value={{ hqvState: state, hqvDispatch: dispatch }}>
          <Layout
            metaNames={metaNames}
            metaProps={metaProperties}
            title={title}
            staticComponentClientLibsCSS={staticComponentClientLibsCSS}
            staticComponentClientLibsJS={staticComponentClientLibsJS}
            isAuthorMode={isAuthorMode}
            gridCss={gridCss || ''}
            hreflangList={hreflangList}
            canonical={canonical}
            USE_MOCK_MODEL={process.env['USE_MOCK_MODEL'] === 'true'}
          >
            <section>
              <ResponsiveGrid
                key={pagePath}
                {...respGridModel}
                model={respGridModel}
                pagePath={pagePath}
                itemPath="root/responsivegrid"
              />
            </section>
          </Layout>
        </HQVContext.Provider>
      </ApolloProvider>
    </ErrorHandlerAlert>
  );
}

const shopSpecificData = async (context: any, parallelCallsResponse: any, interceptorResponse: any) => {
  const { log } = global.loggerInstance('findHotels:');
  //any page specific code can be written here.
  const IS_LOCAL_DEV = process.env['IS_LOCAL_DEV'];
  const deployedEnvType = getDeployedEnvType();
  const resolvedUrl = context?.resolvedUrl ?? context?.req?.url;
  const homepageRedirect = {
    redirect: {
      permanent: false,
      destination: addSubDirectoryPrefix(process.env['HOMEPAGE_REDIRECT_URL']),
    },
  };
  const { req } = context;
  let preprocessorURL = process.env['NEXT_PUBLIC_PRE_PROCESSING_URL_FINDHOTEL'];

  const preHeader: Record<string, string> = {
    'Content-Type': 'application/json',
    'x-request-id': req?.headers?.['x-request-id'] || '',
    Cookie: `sessionID=${req?.cookies?.sessionID ?? ''};UserIdToken=${
      req?.cookies?.UserIdToken ? req?.cookies?.UserIdToken : ''
    };authStateToken=${req?.cookies?.authStateToken ? req?.cookies?.authStateToken : ''}`,
    'X-Host': req?.headers?.['x-host'] ?? '',
    'accept-language': req?.headers?.['accept-language'] || process.env['NEXT_PUBLIC_DEFAULT_LANG'] || '',
  };

  if (resolvedUrl.includes('?')) {
    preprocessorURL = `${process.env['NEXT_PUBLIC_PRE_PROCESSING_URL_FINDHOTEL']}?${resolvedUrl.substring(
      resolvedUrl.indexOf('?') + 1
    )}`;
  }

  let sessionData;
  if (IS_LOCAL_DEV !== 'true') {
    try {
      const response = await axios.post(preprocessorURL ?? '', interceptorResponse, {
        headers: preHeader,
      });
      sessionData = response?.data?.sessionData;
      useGlobalStore.setState({ sessionData });
      // Redirect user to homepage if user directly loads findHotels.mi
      const sessionDataObject = sessionData?.data?.AriesSearch?.searchCriteria
        ? sessionData?.data?.AriesSearch?.searchCriteria
        : sessionData?.cacheData?.data?.AriesSearch?.searchCriteria;

      if (resolvedUrl.includes('findHotels') && !sessionDataObject) {
        Cookies.remove('sessionID');
        return {
          error: {
            value: true,
            redirect: homepageRedirect,
          },
        };
      }
    } catch (error: any) {
      log.error(`shop-app API call failed: ${error}`);
      log.debug(`shop-app API call failed with resp header: ${inspect(error?.response?.headers?.location)}`);
      if (error?.response?.headers?.location) {
        return {
          error: {
            value: true,
            redirect: {
              redirect: {
                permanent: false,
                destination: error?.response?.headers?.location,
              },
            },
          },
        };
      } else {
        return {
          error: {
            value: true,
            redirect: {
              redirect: {
                permanent: false,
                destination: process.env.REDIRECT_URL,
              },
            },
          },
        };
      }
    }
  } else {
    const response = await preProcessorCallMocked();
    sessionData = response.sessionData;
  }
  //shop app call end

  const {
    SESSION_APP_GET_CALL_URL,
    SESSION_APP_POST_CALL_URL,
    MI_DATALAYER_APP_URL,
    NEXT_PUBLIC_DATALAYER_SITENAME,
    NEXT_PUBLIC_DATALAYER_PRODUCT,
    NEXT_PUBLIC_DATALAYER_APPLICATION,
    NEXT_PUBLIC_DATALAYER_TEMPLATE,
    NEXT_PUBLIC_DATALAYER_VARIATION,
    NEXT_PUBLIC_ORIENTATION_LANDSCAPE,
    NEXT_PUBLIC_ORIENTATION_PORTRAIT,
    NEXT_PUBLIC_PATHTYPE_NEW,
    NEXT_PUBLIC_PATHTYPE_LEGACY,
    NEXT_PUBLIC_SEARCH_SINGLE_RATE,
    NEXT_PUBLIC_RAM_TILE_NAME,
    NEXT_PUBLIC_PAGE_BREAKPT,
    NEXT_PUBLIC_SEARCH_MAPVIEW,
    NEXT_PUBLIC_SEARCH_LISTVIEW,
    NEXT_PUBLIC_PRE_PROCESSING_URL,
    NEXT_PUBLIC_DYNAMIC_ROUTING_URL_CLIENT,
    REDIRECT_URL,
    GOOGLE_MAP_API_KEY,
    NEXT_PUBLIC_PREFIX,
    CACHE_CLEAR_URL,
    NEXT_PUBLIC_AVAILABILITY_SEARCH_URL,
    ENABLE_TARGET_SEGMENT_CALL,
    TARGET_SEGMENT_PROPERTY_TOKEN,
    TARGET_SEGMENT_MBOX_NAME,
  } = getProcessEnvs();
  const featureFlagLogo = { experience_mcom_logo_automation: false };
  const findHotelsRemoteSPA = true;
  return {
    featureFlagLogo,
    SHOP_UXL_CALL_SPLIT: process.env['SHOP_UXL_CALL_SPLIT'],
    operationSignatures,
    deployedEnvType,
    sessionFetchedData: sessionData,
    preHeader,
    SESSION_APP_GET_CALL_URL,
    SESSION_APP_POST_CALL_URL,
    NEXT_PUBLIC_PRE_PROCESSING_URL,
    NEXT_PUBLIC_DYNAMIC_ROUTING_URL_CLIENT,
    datalayerParams: {
      MI_DATALAYER_APP_URL,
      NEXT_PUBLIC_DATALAYER_SITENAME,
      NEXT_PUBLIC_DATALAYER_PRODUCT,
      NEXT_PUBLIC_DATALAYER_APPLICATION,
      NEXT_PUBLIC_DATALAYER_TEMPLATE,
      NEXT_PUBLIC_DATALAYER_VARIATION,
    },
    datalayerProperties: {
      NEXT_PUBLIC_ORIENTATION_LANDSCAPE,
      NEXT_PUBLIC_ORIENTATION_PORTRAIT,
      NEXT_PUBLIC_PATHTYPE_NEW,
      NEXT_PUBLIC_PATHTYPE_LEGACY,
      NEXT_PUBLIC_SEARCH_SINGLE_RATE,
      NEXT_PUBLIC_RAM_TILE_NAME,
      NEXT_PUBLIC_PAGE_BREAKPT,
      NEXT_PUBLIC_SEARCH_MAPVIEW,
      NEXT_PUBLIC_SEARCH_LISTVIEW,
    },
    REDIRECT_URL,
    GOOGLE_MAP_API_KEY,
    NEXT_PUBLIC_PREFIX,
    CACHE_CLEAR_URL,
    NEXT_PUBLIC_AVAILABILITY_SEARCH_URL,
    ENABLE_TARGET_SEGMENT_CALL,
    TARGET_SEGMENT_PROPERTY_TOKEN,
    TARGET_SEGMENT_MBOX_NAME,
    findHotelsRemoteSPA,
  };
};

const mockModel = async () => {
  if (process.env['NODE_ENV'] === 'development') {
    // nx-ignore-next-line
    const { FindHotelModelPod2 } = await import('@marriott/mi-shop-components'); //Load mock model dynamically only for dev mode
    return FindHotelModelPod2;
  }
  return {};
};

const parallelCallsArrFromPage = [{ api: 'getFeatureFlag', apiHandler: getFeatureFlagData() }];

//SSR Render
export const getServerSideProps: GetServerSideProps<any> = prePageCall(
  shopSpecificData,
  mockModel,
  false,
  false,
  true,
  parallelCallsArrFromPage
);
